// ShellChromeContext.js — Section identity + action slots for shell chrome

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { useIsMobile } from 'hooks/useDeviceDetect';

const ShellChromeContext = createContext(null);

/**
 * ShellChromeProvider
 *
 * Holds optional section identity (title + icon) for SectionToolbar, plus DOM
 * slots where sections can portal custom identity, action controls, and
 * GlobalToolbar leading controls (e.g. Back).
 *
 * @param {object} props
 * @param {ReactNode} props.children
 * @returns {ReactNode}
 */
export const ShellChromeProvider = ({ children }) => {
  const [identity, setIdentityState] = useState(null);
  const [actionsSlotEl, setActionsSlotEl] = useState(null);
  const [identitySlotEl, setIdentitySlotEl] = useState(null);
  const [globalLeadingSlotEl, setGlobalLeadingSlotEl] = useState(null);
  // Phone top-bar leading slot (Back replaces drawer menu when filled).
  const [mobileLeadingSlotEl, setMobileLeadingSlotEl] = useState(null);
  const [hasCustomIdentity, setHasCustomIdentity] = useState(false);

  const setShellChrome = useCallback((next) => {
    setIdentityState((previous) => {
      const title = String(next?.title || '').trim();
      const typeKey = String(next?.typeKey || '').trim();
      const iconKey = String(next?.iconKey || '').trim();
      const iconResource = String(next?.iconResource || '').trim();
      if (!title && !typeKey && !iconKey && !iconResource) return null;

      const resolved = {
        title,
        typeKey,
        iconKey,
        iconResource,
      };

      if (
        previous
        && previous.title === resolved.title
        && previous.typeKey === resolved.typeKey
        && previous.iconKey === resolved.iconKey
        && previous.iconResource === resolved.iconResource
      ) {
        return previous;
      }

      return resolved;
    });
  }, []);

  const clearShellChrome = useCallback(() => {
    setIdentityState(null);
  }, []);

  const value = useMemo(() => ({
    identity,
    actionsSlotEl,
    setActionsSlotEl,
    identitySlotEl,
    setIdentitySlotEl,
    globalLeadingSlotEl,
    setGlobalLeadingSlotEl,
    mobileLeadingSlotEl,
    setMobileLeadingSlotEl,
    hasCustomIdentity,
    setHasCustomIdentity,
    setShellChrome,
    clearShellChrome,
  }), [
    actionsSlotEl,
    clearShellChrome,
    globalLeadingSlotEl,
    hasCustomIdentity,
    identity,
    identitySlotEl,
    mobileLeadingSlotEl,
    setShellChrome,
  ]);

  return (
    <ShellChromeContext.Provider value={value}>
      {children}
    </ShellChromeContext.Provider>
  );
};

/**
 * useShellChrome
 *
 * @returns {object}
 */
export const useShellChrome = () => {
  const context = useContext(ShellChromeContext);
  if (!context) {
    throw new Error('useShellChrome must be used within ShellChromeProvider');
  }
  return context;
};

/**
 * Publishes section identity into SectionToolbar while mounted.
 *
 * @param {object} options
 * @param {string} [options.title]
 * @param {string} [options.typeKey] - Node type key (TypeIcon).
 * @param {string} [options.iconKey] - Catalog icon key (Icon).
 * @param {string} [options.iconResource] - Image URL (connector logos, etc.).
 * @param {boolean} [options.enabled=true]
 */
export const usePublishShellChrome = ({
  title = '',
  typeKey = '',
  iconKey = '',
  iconResource = '',
  enabled = true,
} = {}) => {
  const { setShellChrome, clearShellChrome } = useShellChrome();

  useEffect(() => {
    if (!enabled) {
      clearShellChrome();
      return undefined;
    }

    setShellChrome({ title, typeKey, iconKey, iconResource });
    return () => clearShellChrome();
  }, [
    clearShellChrome,
    enabled,
    iconKey,
    iconResource,
    setShellChrome,
    title,
    typeKey,
  ]);
};

/**
 * ShellGlobalLeading
 *
 * Portals controls into GlobalToolbar (desktop) or MobileTopBar (phone)
 * to the left of Find (e.g. Back). On phone, a filled slot hides the
 * drawer hamburger so Back takes the top-left position.
 *
 * @param {object} props
 * @param {ReactNode} props.children
 * @param {boolean} [props.enabled=true]
 * @param {boolean} [props.fallback=false]
 * @returns {ReactNode}
 */
export const ShellGlobalLeading = ({
  children,
  enabled = true,
  fallback = false,
}) => {
  const { globalLeadingSlotEl, mobileLeadingSlotEl } = useShellChrome();
  const isMobile = useIsMobile();
  const slotEl = isMobile ? mobileLeadingSlotEl : globalLeadingSlotEl;

  if (!enabled || children == null) {
    return null;
  }

  if (!slotEl) {
    return fallback ? children : null;
  }

  return createPortal(children, slotEl);
};

/**
 * ShellSectionIdentity
 *
 * Portals custom section identity (e.g. editable node title) into SectionToolbar
 * on desktop, replacing the default icon + title text.
 *
 * @param {object} props
 * @param {ReactNode} props.children
 * @param {boolean} [props.enabled=true]
 * @param {boolean} [props.fallback=false]
 * @returns {ReactNode}
 */
export const ShellSectionIdentity = ({
  children,
  enabled = true,
  fallback = false,
}) => {
  const { identitySlotEl, setHasCustomIdentity } = useShellChrome();
  const isMobile = useIsMobile();
  const shouldPortal = Boolean(enabled && !isMobile && identitySlotEl);

  useLayoutEffect(() => {
    if (!enabled || isMobile) {
      setHasCustomIdentity(false);
      return undefined;
    }
    setHasCustomIdentity(true);
    return () => setHasCustomIdentity(false);
  }, [enabled, isMobile, setHasCustomIdentity]);

  if (!enabled || children == null) {
    return null;
  }

  if (!shouldPortal) {
    return fallback ? children : null;
  }

  return createPortal(children, identitySlotEl);
};

/**
 * ShellSectionActions
 *
 * Portals section action controls into SectionToolbar on desktop. When the
 * shell slot is unavailable (mobile, or toolbar not mounted), optionally
 * renders children inline via `fallback`.
 *
 * @param {object} props
 * @param {ReactNode} props.children
 * @param {boolean} [props.enabled=true]
 * @param {boolean} [props.fallback=false] - Render children inline when not portaling.
 * @returns {ReactNode}
 */
export const ShellSectionActions = ({
  children,
  enabled = true,
  fallback = false,
}) => {
  const { actionsSlotEl } = useShellChrome();
  const isMobile = useIsMobile();

  if (!enabled || children == null) {
    return null;
  }

  if (isMobile || !actionsSlotEl) {
    return fallback ? children : null;
  }

  return createPortal(children, actionsSlotEl);
};

export default ShellChromeContext;
